import { get2, patch, put2 } from "@/shared/axios";
import useBranchId from "@/shared/hooks/useBranchId";
import { getValueByAll, getValueByAllInArray } from "@/shared/utils/helpers";
import {
  PrinterSettingsData,
  PrinterSettingsResponse,
} from "@/shared/utils/interfaces";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { errorToast } from "../utils/helpers";

const usePrinterSettingsHook = () => {
  const branch = useBranchId();
  const { t } = useTranslation();

  const getPrinterSettings = useQuery({
    queryKey: ["printerSettings", branch],
    queryFn: () => {
      return get2(`internal/secure/pos/v1/printerSettings/${branch}`);
    },
    select: (data: PrinterSettingsResponse) => {
      const {
        printers,
        print_quantities,
        chosen_printers,
        receipt_setting_ids,
        configuration_names,
        branch_id,
        is_active,
        item_id,
      } = data;

      const allPrinters = (getValueByAllInArray(printers) as string[]) || [];
      const chosenPrinters =
        (getValueByAllInArray(chosen_printers) as string[]) || [];

      const printQuantities = getValueByAllInArray(print_quantities) || [];
      const receiptSettingIds = getValueByAllInArray(receipt_setting_ids) || [];
      const configurationNames =
        getValueByAllInArray(configuration_names) || [];

      const nonChosenPrinters = allPrinters.filter(
        (p) => !chosenPrinters.includes(p),
      );

      let retPrinters = {};

      chosenPrinters?.forEach((chosen, index) => {
        const newData = {
          quantity: printQuantities[index],
          receiptId: receiptSettingIds[index],
          configName: configurationNames[index],
        };

        if (retPrinters[chosen]) {
          retPrinters = {
            ...retPrinters,
            [chosen]: [...retPrinters[chosen], newData],
          };
        } else {
          retPrinters = {
            ...retPrinters,
            [chosen]: [newData],
          };
        }
      });

      nonChosenPrinters.forEach((nonChosen) => {
        retPrinters = {
          ...retPrinters,
          [nonChosen]: [],
        };
      });

      const retVal = {
        branchId: getValueByAll(branch_id),
        isActive: getValueByAll(is_active) || false,
        itemId: getValueByAll(item_id),
        chosenPrinters: chosenPrinters,
        printers: retPrinters,
      };
      return retVal as PrinterSettingsData;
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  useEffect(() => {
    if (getPrinterSettings.isError) {
      errorToast();
    }
  }, [getPrinterSettings.isError]);

  const postPrinterSettings = useMutation({
    mutationFn: (data: any) => {
      return put2(`internal/secure/pos/v1/printerSettings`, data);
    },
    onSuccess: async () => {
      toast.success(t("success.success"));
      getPrinterSettings.refetch();
    },
    onError: async () => {
      errorToast();
    },
  });

  const patchPrinters = useMutation({
    mutationFn: (printers: { printers: [] }) => {
      return patch(
        `internal/secure/pos/v1/printerSettings/printers/${branch}`,
        {
          printers: printers.printers,
        },
      );
    },
    onSuccess: () => {
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast();
    },
  });

  return {
    getPrinterSettings,
    postPrinterSettings,
    patchPrinters,
  };
};

export default usePrinterSettingsHook;
